(() => {
  const script = document.currentScript || document.querySelector('script[data-doofinder-load-more]');
  const configuredBatchSize = Number.parseInt(script?.dataset.pageSize || '', 10);
  const batchSize = Number.isFinite(configuredBatchSize) && configuredBatchSize > 0 ? configuredBatchSize : 24;
  const language = document.documentElement.lang.split('-')[0];
  const fallbackLabels = {
    de: { load: 'Mehr laden', loading: 'Mehr laden…', of: 'von', result: 'Ergebnis', results: 'Ergebnisse' },
    en: { load: 'Load more', loading: 'Loading…', of: 'of', result: 'result', results: 'results' },
    it: { load: 'Carica altro', loading: 'Carica altro…', of: 'di', result: 'risultato', results: 'risultati' },
  }[language] || { load: 'Load more', loading: 'Loading…', of: 'of', result: 'result', results: 'results' };
  const labels = {
    load: script?.dataset.labelLoad || fallbackLabels.load,
    loading: script?.dataset.labelLoading || fallbackLabels.loading,
    of: script?.dataset.labelOf || fallbackLabels.of,
    result: script?.dataset.labelResult || fallbackLabels.result,
    results: script?.dataset.labelResults || fallbackLabels.results,
  };

  const selectors = {
    content: '.dfd-content',
    footer: '#shopify-section-section_footer',
    grid: '.dfd-results-grid',
    root: '.dfd-root',
    searchInput: '.dfd-searchbox-input',
    sentinel: '[data-dfd-role~="scroll-sentinel"]',
    total: '.dfd-meta strong, .dfd-meta > .dfd-title-sm',
  };
  const classes = {
    hiddenResult: 'oc-doofinder-result-hidden',
    loadingSentinel: 'oc-doofinder-sentinel-loading',
    pausedSentinel: 'oc-doofinder-sentinel-paused',
  };
  const loadTimeoutMs = 8000;
  const sentinelActivationDelayMs = 600;
  const sentinelActivationWindowMs = 1000;

  const state = {
    controls: null,
    footerMarker: null,
    footerSection: null,
    grid: null,
    activationDelay: null,
    loadStartCount: 0,
    loadTimeout: null,
    loading: false,
    observedRoot: null,
    resultSetKey: '',
    sentinelPauseTimeout: null,
    syncQueued: false,
    visibleLimit: batchSize,
  };

  function getResultElements(grid) {
    return Array.from(grid?.children || []).filter((element) => element.querySelector('.dfd-card'));
  }

  function getTotal(grid, resultCount) {
    const totalElement = grid?.parentElement?.querySelector(selectors.total);
    const parsedTotal = Number.parseInt((totalElement?.textContent || '').replace(/\D/g, ''), 10);

    return Number.isFinite(parsedTotal) ? parsedTotal : resultCount;
  }

  function getResultSetKey(grid, results, total) {
    const query = document.querySelector(selectors.searchInput)?.value.trim() || '';
    const firstResultId = results[0]?.id || results[0]?.querySelector('.dfd-card')?.id || '';

    return `${query}\u0000${total}\u0000${firstResultId}\u0000${grid.id}`;
  }

  function pauseSentinel(sentinel) {
    if (!sentinel) return;

    sentinel.classList.remove(classes.loadingSentinel);
    sentinel.classList.add(classes.pausedSentinel);
    sentinel.style.setProperty('display', 'none', 'important');
    sentinel.style.removeProperty('height');
    sentinel.style.removeProperty('transform');
    sentinel.style.removeProperty('width');
  }

  function clearLoadState() {
    window.clearTimeout(state.activationDelay);
    window.clearTimeout(state.loadTimeout);
    window.clearTimeout(state.sentinelPauseTimeout);
    state.activationDelay = null;
    state.loadTimeout = null;
    state.loading = false;
    state.loadStartCount = 0;
    state.sentinelPauseTimeout = null;
  }

  function restoreFooter() {
    if (!state.footerSection) return;

    const content = state.footerSection.closest(selectors.content);
    content?.classList.remove('oc-doofinder-has-footer');
    state.footerSection.classList.remove('oc-doofinder-footer');

    if (
      state.footerMarker?.parentNode &&
      (state.footerSection.parentNode !== state.footerMarker.parentNode ||
        state.footerMarker.nextSibling !== state.footerSection)
    ) {
      state.footerMarker.parentNode.insertBefore(state.footerSection, state.footerMarker.nextSibling);
    }
  }

  function mountFooter(content) {
    const footerSection = state.footerSection || document.querySelector(selectors.footer);

    if (!footerSection || !content) return;

    if (!state.footerSection) {
      state.footerSection = footerSection;
      state.footerMarker = document.createComment('Officine footer position');
      footerSection.parentNode?.insertBefore(state.footerMarker, footerSection);
    }

    if (footerSection.parentElement !== content) content.append(footerSection);

    content.classList.add('oc-doofinder-has-footer');
    footerSection.classList.add('oc-doofinder-footer');
  }

  function removeControls() {
    state.controls?.container.remove();
    state.controls = null;
  }

  function resetPagination() {
    const sentinel = state.grid?.parentElement?.querySelector(selectors.sentinel);

    pauseSentinel(sentinel);
    state.grid?.querySelectorAll(`.${classes.hiddenResult}`).forEach((result) => {
      result.classList.remove(classes.hiddenResult);
    });
    clearLoadState();
    restoreFooter();
    removeControls();
    state.grid = null;
    state.resultSetKey = '';
  }

  function resetForResultSet(grid, resultSetKey) {
    clearLoadState();
    restoreFooter();
    removeControls();
    state.grid = grid;
    state.resultSetKey = resultSetKey;
    state.visibleLimit = batchSize;
  }

  function createControls(grid) {
    const container = document.createElement('div');
    const status = document.createElement('p');
    const button = document.createElement('button');
    const buttonLabel = document.createElement('span');

    container.className = 'oc-doofinder-pagination';
    container.dataset.doofinderPagination = '';

    status.className = 'oc-doofinder-pagination__status';
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');

    button.className = 'oc-doofinder-pagination__button actionbtn button button--primary';
    button.type = 'button';
    buttonLabel.className = 'oc-doofinder-pagination__button-label load-more-text uppercase text-white';
    buttonLabel.textContent = labels.load;
    button.append(buttonLabel);
    if (grid.id) button.setAttribute('aria-controls', grid.id);

    button.addEventListener('click', () => {
      const results = getResultElements(state.grid);
      const total = getTotal(state.grid, results.length);

      state.visibleLimit = Math.min(state.visibleLimit + batchSize, total);
      sync();
    });

    container.append(status, button);
    grid.insertAdjacentElement('afterend', container);
    state.controls = { button, buttonLabel, container, status };

    return state.controls;
  }

  function getControls(grid) {
    if (state.controls?.container.isConnected && state.controls.container.previousElementSibling === grid) {
      return state.controls;
    }

    return createControls(grid);
  }

  function updateControls(grid, shown, total) {
    const controls = getControls(grid);
    const hasMore = shown < total;
    const resultLabel = total === 1 ? labels.result : labels.results;
    const statusText = `${shown} ${labels.of} ${total} ${resultLabel}`;
    const isButtonDisabled = hasMore && state.loading;
    const isContainerHidden = total === 0;
    const busyState = state.loading ? 'true' : 'false';
    const buttonText = state.loading ? labels.loading : labels.load;

    if (controls.status.textContent !== statusText) controls.status.textContent = statusText;
    if (controls.button.hidden !== !hasMore) controls.button.hidden = !hasMore;
    if (controls.button.disabled !== isButtonDisabled) controls.button.disabled = isButtonDisabled;
    if (controls.button.getAttribute('aria-busy') !== busyState) {
      controls.button.setAttribute('aria-busy', busyState);
    }
    if (controls.buttonLabel.textContent !== buttonText) controls.buttonLabel.textContent = buttonText;
    if (controls.container.hidden !== isContainerHidden) controls.container.hidden = isContainerHidden;
  }

  function activateSentinel(content, sentinel) {
    sentinel.classList.remove(classes.pausedSentinel);
    sentinel.classList.add(classes.loadingSentinel);
    sentinel.style.setProperty('display', 'block', 'important');
    sentinel.style.setProperty('height', '1px', 'important');
    sentinel.style.setProperty('width', '1px', 'important');

    window.requestAnimationFrame(() => {
      if (!sentinel.isConnected || !content.isConnected || !state.loading) return;

      // A control insertion can queue a sync in the same frame. Ensure that
      // activation wins that race before measuring the sentinel.
      sentinel.classList.remove(classes.pausedSentinel);
      sentinel.classList.add(classes.loadingSentinel);
      sentinel.style.setProperty('display', 'block', 'important');

      const contentRect = content.getBoundingClientRect();
      const sentinelRect = sentinel.getBoundingClientRect();
      const targetY = contentRect.top + contentRect.height / 2;
      const translateY = targetY - sentinelRect.top;

      sentinel.style.setProperty('transform', `translateY(${translateY}px)`, 'important');

      // On the first result page Doofinder reconnects its observer after
      // 500ms, then debounces the load-more event by another 200ms.
      window.clearTimeout(state.sentinelPauseTimeout);
      state.sentinelPauseTimeout = window.setTimeout(() => {
        pauseSentinel(sentinel);
        state.sentinelPauseTimeout = null;
      }, sentinelActivationWindowMs);
    });
  }

  function requestNextPage(grid, resultCount) {
    const content = grid.closest(selectors.content);
    const sentinel = content?.querySelector(selectors.sentinel);

    if (!content || !sentinel || state.loading) return;

    state.loading = true;
    state.loadStartCount = resultCount;
    updateControls(grid, Math.min(resultCount, state.visibleLimit), getTotal(grid, resultCount));

    // The first result patch temporarily disconnects Doofinder's observer.
    // Wait for its documented reconnect window before exposing the sentinel.
    state.activationDelay = window.setTimeout(() => {
      state.activationDelay = null;
      if (state.loading) activateSentinel(content, sentinel);
    }, sentinelActivationDelayMs);

    state.loadTimeout = window.setTimeout(() => {
      pauseSentinel(sentinel);
      clearLoadState();
      queueSync();
    }, loadTimeoutMs);
  }

  function sync() {
    state.syncQueued = false;

    const root = document.querySelector(selectors.root);
    const openRoot = root?.classList.contains('dfd-animate') ? root : null;

    if (root && !openRoot) {
      resetPagination();
      return;
    }

    const grid = openRoot?.querySelector(selectors.grid) || (!root ? document.querySelector(selectors.grid) : null);

    if (!grid) {
      resetPagination();
      return;
    }

    const results = getResultElements(grid);
    const total = getTotal(grid, results.length);
    const resultSetKey = getResultSetKey(grid, results, total);

    if (state.grid !== grid || state.resultSetKey !== resultSetKey) {
      resetForResultSet(grid, resultSetKey);
    }

    results.forEach((result, index) => {
      result.classList.toggle(classes.hiddenResult, index >= state.visibleLimit);
    });

    const targetCount = Math.min(state.visibleLimit, total);
    const shown = Math.min(results.length, targetCount);

    if (state.loading && (results.length > state.loadStartCount || results.length >= targetCount)) {
      clearLoadState();
    }

    const sentinel = grid.parentElement?.querySelector(selectors.sentinel);
    if (!state.loading) pauseSentinel(sentinel);

    updateControls(grid, shown, total);

    if (total > 0 && shown >= targetCount && !state.loading) {
      mountFooter(grid.closest(selectors.content));
    } else {
      restoreFooter();
    }

    if (shown < targetCount && !state.loading) {
      window.setTimeout(() => requestNextPage(grid, results.length), 0);
    }
  }

  function queueSync() {
    if (state.syncQueued) return;

    state.syncQueued = true;
    window.requestAnimationFrame(sync);
  }

  function isRelevantRootMutation(mutation) {
    const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;

    return !target?.closest('[data-doofinder-pagination], .oc-doofinder-footer');
  }

  const rootObserver = new MutationObserver((mutations) => {
    if (mutations.some(isRelevantRootMutation)) queueSync();
  });
  const rootStateObserver = new MutationObserver(queueSync);

  function refreshRootObserver() {
    const root = document.querySelector(selectors.root);

    if (root === state.observedRoot) return;

    rootObserver.disconnect();
    rootStateObserver.disconnect();
    state.observedRoot = root;

    if (root) {
      rootObserver.observe(root, { childList: true, subtree: true });
      rootStateObserver.observe(root, { attributeFilter: ['class'], attributes: true });
    }
    queueSync();
  }

  const rootDiscoveryObserver = new MutationObserver(refreshRootObserver);
  rootDiscoveryObserver.observe(document.body, { childList: true });

  document.addEventListener('input', (event) => {
    if (event.target.matches?.(selectors.searchInput)) queueSync();
  });

  [
    'doofinder.layer.load',
    'doofinder.layer.search',
    'doofinder.layer.update',
    'doofinder.show',
    'doofinder.search',
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      refreshRootObserver();
      queueSync();
    });
  });

  document.addEventListener('doofinder.hide', restoreFooter);

  refreshRootObserver();
})();
